import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../shared/api';
import { Modal } from '../../ui/Modal';

/**
 * Self-service password change. Available to every authenticated user via
 * the sidebar user-info area. Verifies the current password before
 * accepting the new one — the server enforces this too, but checking
 * client-side avoids a round-trip on the obvious typo case.
 *
 * On success, the password is updated server-side but the user's existing
 * JWT remains valid until its TTL (12h). We don't force a re-login —
 * that's a defensible choice either way; the current value is "frictionless,
 * because the user just proved they know both old and new password."
 */
export function ChangeOwnPasswordModal({ onClose }: { onClose: () => void }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: () =>
      api
        .post('/users/me/change-password', { currentPassword, newPassword })
        .then((r) => r.data),
    onSuccess: () => {
      toast.success('Password changed');
      onClose();
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message ?? 'Change failed');
    },
  });

  const canSubmit =
    currentPassword.length > 0 &&
    newPassword.length >= 8 &&
    confirmPassword === newPassword &&
    !change.isPending;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError('New password confirmation does not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (newPassword === currentPassword) {
      setError('New password must be different from current password');
      return;
    }
    change.mutate();
  }

  return (
    <Modal open onClose={onClose} title="Change your password" size="md">
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="label">Current password</label>
          <input
            type="password"
            className="input"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoFocus
            autoComplete="current-password"
          />
        </div>
        <div>
          <label className="label">New password (≥8 chars)</label>
          <input
            type="password"
            className="input"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
          />
        </div>
        <div>
          <label className="label">Confirm new password</label>
          <input
            type="password"
            className="input"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
          />
          {confirmPassword.length > 0 && confirmPassword !== newPassword && (
            <p className="mt-1 text-2xs text-rose-700">Passwords don't match</p>
          )}
        </div>
        {error && (
          <div className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>
        )}
        <footer className="mt-4 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={!canSubmit}>
            {change.isPending ? 'Changing…' : 'Change password'}
          </button>
        </footer>
      </form>
    </Modal>
  );
}
